import { useFadeIn, FadeUp } from '../hooks/useFadeIn';

const AboutSection = ({ onContactClick, aboutContent }) => {
  const gold = '#C6A24A';
  const goldDim = 'rgba(198,162,74,0.18)';
  const dark = '#0C0C0C';
  const muted = 'rgba(255,255,255,0.42)';
  const font = "'Oswald', 'Impact', 'Arial Narrow', sans-serif'";
  const SQUIRE_BOOKING_URL = 'https://getsquire.com/booking/brands/bro-code-barbershop';
  const [headRef, headVisible] = useFadeIn(0.1);
  const [imgRef, imgVisible] = useFadeIn(0.1);

  const imageUrl = aboutContent?.aboutImage || 'https://brocode.b-cdn.net/DSC00403.webp';

  // Mobile check
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  return (
    <section
      id="about"
      style={{
        background: isMobile
           ? `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.75)), url(${imageUrl}) center/cover no-repeat` // mobile: background + fade // mobile: background
          : dark,                                    // desktop: solid color
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Top Info Row */}
      <div style={{
        borderBottom: `1px solid ${goldDim}`,
        borderTop: `1px solid ${goldDim}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 5vw',
        gap: '1rem',
      }}>
        {['EST. 2020', '///', 'BRO CODE BARBERSHOP', '///', 'PRECISION · STYLE · RESPECT'].map((item, i) => (
          <span key={i} style={{
            fontFamily: font,
            fontSize: 'clamp(0.55rem, 1.1vw, 0.75rem)',
            fontWeight: 600,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: i % 2 === 1 ? gold : muted,
            whiteSpace: 'nowrap',
          }}>{item}</span>
        ))}
      </div>

      {/* Main Two Column */}
      <div className="about__two-col" style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        minHeight: '88vh',
      }}>
        {/* Text Column */}
        <div ref={headRef} style={{
          padding: 'clamp(3rem,7vw,7rem) clamp(2rem,5vw,5rem)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: isMobile ? 'none' : `1px solid ${goldDim}`,
          opacity: headVisible ? 1 : 0,
          transform: headVisible ? 'translateX(0)' : 'translateX(-50px)',
          transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <p style={{ fontFamily: font, fontSize: 'clamp(0.6rem, 1vw, 0.78rem)', fontWeight: 600,
            letterSpacing: '0.3em', textTransform: 'uppercase', color: gold, marginBottom: '1.8rem' }}>
            — Who We Are
          </p>
          <h2 style={{ fontFamily: font, fontSize: 'clamp(3.5rem, 7.5vw, 8.5rem)', fontWeight: 800,
            lineHeight: 0.88, textTransform: 'uppercase', letterSpacing: '-0.02em',
            marginBottom: '2.5rem', color: '#fff' }}>
            {aboutContent?.heading1 || "We Don't"}{' '}
            <span style={{ color: gold, fontStyle: 'italic' }}>{aboutContent?.highlight1 || 'Cut.'}</span>
            <br />{aboutContent?.heading2 || 'We'}{' '}
            <span style={{ color: gold, fontStyle: 'italic' }}>{aboutContent?.highlight2 || 'Sculpt.'}</span>
          </h2>
          <div style={{ width: '3.5rem', height: '2px', background: `linear-gradient(90deg, ${gold}, transparent)`, marginBottom: '2rem' }} />
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(0.95rem, 1.4vw, 1.1rem)',
            lineHeight: 1.8, color: muted, maxWidth: '480px', marginBottom: '1.5rem' }}>
            {aboutContent?.para1 || "Brocode isn't your average barbershop. We're a culture — built on sharp technique, real conversation, and the belief that every man deserves to walk out looking like the best version of himself. No shortcuts. No excuses."}
          </p>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(0.95rem, 1.4vw, 1.1rem)',
            lineHeight: 1.8, color: muted, maxWidth: '480px', marginBottom: '3rem' }}>
            {aboutContent?.para2 || "Our barbers train obsessively, stay ahead of every trend, and treat each client like family. Whether it's a clean taper, a full beard sculpt, or a straight razor finish — we go hard on every single cut, every single time."}
          </p>
        </div>

        {/* Image Column (desktop only) */}
        {!isMobile && (
          <div ref={imgRef} style={{
            position: 'relative',
            overflow: 'hidden',
            opacity: imgVisible ? 1 : 0,
            transform: imgVisible ? 'scale(1)' : 'scale(1.04)',
            transition: 'opacity 1.1s, transform 1.4s',
          }}>
            <img
              src={imageUrl}
              alt="Barber giving a precision haircut inside Bro Code Barbershop in Abbotsford"
              loading="lazy"
              decoding="async"
              width="800"
              height="1000"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        )}
      </div>

      {/* CTA */}
      <div style={{
        borderTop: `1px solid ${goldDim}`,
        padding: 'clamp(3rem,6vw,5rem) 5vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '2rem',
        flexWrap: 'wrap'
      }}>
        <FadeUp delay={0}>
          <p style={{ fontFamily: font, fontSize: 'clamp(1.8rem, 4vw, 3.5rem)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-0.02em', lineHeight: 1, color: '#fff' }}>
            Ready to{' '}<span style={{ color: gold, fontStyle: 'italic' }}>level up?</span>
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
  <a href="https://getsquire.com/booking/brands/bro-code-barbershop" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} onClick={() => {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-11480037647/YvQ4CIOw3I0cEI_yjeIq'
    });
  }}>
            <button style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.8rem',
              fontFamily: font,
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: dark,
              background: gold,
              border: 'none',
              padding: '1.1rem 2.5rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Book your visit
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </a>
        </FadeUp>
      </div>
    </section>
  );
};

export default AboutSection;